import { useState } from "react";
import { adminUpdateQuote, adminExportQuotesCsvUrl } from "@/lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";

const STATUSES = ["submitted", "reviewing", "routed", "contacted", "won", "lost", "closed", "cancelled"];

const STATUS_STYLE = {
    submitted: "bl-badge bl-badge-medium",
    reviewing: "bl-badge bl-badge-neutral",
    new: "bl-badge bl-badge-medium",
    routed: "bl-badge bl-badge-neutral",
    contacted: "bl-badge bl-badge-neutral",
    won: "bl-badge bl-badge-low",
    lost: "bl-badge bl-badge-high",
    closed: "bl-badge bl-badge-neutral",
    cancelled: "bl-badge bl-badge-neutral",
};

function StatusBadge({ status }) {
    const s = status || "new";
    return (
        <span className={STATUS_STYLE[s] || "bl-badge"} data-testid={`quote-status-${s}`}>
            {s}
        </span>
    );
}

function QuoteRow({ quote, onChanged }) {
    const [editing, setEditing] = useState(false);
    const [status, setStatus] = useState(quote.status || "new");
    const [routedTo, setRoutedTo] = useState(quote.routed_to || "");
    const [notes, setNotes] = useState(quote.notes_internal || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        try {
            setSaving(true);
            await adminUpdateQuote(quote.quote_id, {
                status,
                routed_to: routedTo,
                notes_internal: notes,
            });
            toast.success("Quote updated");
            setEditing(false);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <tr
                className="border-b border-bl-soft hover:bg-bl-bg/40"
                data-testid={`quote-row-${quote.quote_id}`}
            >
                <td className="px-5 py-3 font-mono-bl text-xs">{quote.category}</td>
                <td className="px-5 py-3">
                    <div className="text-bl">{quote.name}</div>
                    <div className="text-bl-muted text-xs">{quote.email}</div>
                </td>
                <td className="px-5 py-3 text-bl-muted text-xs">{quote.phone || "—"}</td>
                <td className="px-5 py-3 font-mono-bl text-xs">{quote.zip_code || "—"}</td>
                <td className="px-5 py-3">
                    <StatusBadge status={quote.status} />
                </td>
                <td className="px-5 py-3 text-bl-muted text-xs">{quote.routed_to || "—"}</td>
                <td className="px-5 py-3 text-bl-muted text-xs whitespace-nowrap">
                    {quote.created_at
                        ? new Date(quote.created_at).toLocaleString()
                        : "—"}
                </td>
                <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                        {quote.report_id && (
                            <a
                                href={`/report/${quote.report_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-bl-primary hover:underline"
                                data-testid={`quote-open-report-link-${quote.quote_id}`}
                                title="Open the inspection report that triggered this quote"
                            >
                                Report ↗
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={() => setEditing((v) => !v)}
                            className="text-xs text-bl-primary hover:underline"
                            data-testid={`quote-edit-btn-${quote.quote_id}`}
                        >
                            {editing ? "Close" : "Manage"}
                        </button>
                    </div>
                </td>
            </tr>
            {editing && (
                <tr className="border-b border-bl-soft bg-bl-bg/30">
                    <td colSpan={8} className="px-5 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <label className="text-xs text-bl-muted">
                                Status
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="mt-1 w-full px-2 py-2 bg-bl-surface rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid={`quote-status-select-${quote.quote_id}`}
                                >
                                    {STATUSES.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="text-xs text-bl-muted md:col-span-2">
                                Routed to (partner name)
                                <input
                                    value={routedTo}
                                    onChange={(e) => setRoutedTo(e.target.value)}
                                    placeholder="e.g. Modernize, John Smith Roofing"
                                    className="mt-1 w-full px-2 py-2 bg-bl-surface rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid={`quote-routed-to-input-${quote.quote_id}`}
                                />
                            </label>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={save}
                                    disabled={saving}
                                    className="bl-btn-primary w-full text-sm"
                                    data-testid={`quote-save-btn-${quote.quote_id}`}
                                >
                                    {saving ? "Saving…" : "Save"}
                                </button>
                            </div>
                            <label className="text-xs text-bl-muted md:col-span-4">
                                Internal notes
                                <textarea
                                    rows={2}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Routing context, partner ref ID, follow-up date…"
                                    className="mt-1 w-full px-2 py-2 bg-bl-surface rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid={`quote-notes-internal-input-${quote.quote_id}`}
                                />
                            </label>
                            {quote.notes && (
                                <div className="md:col-span-4 text-xs text-bl-muted bl-card p-3">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted mb-1">
                                        Buyer notes
                                    </div>
                                    {quote.notes}
                                </div>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function QuotesAdmin({ quotes, counts, filter, onFilterChange, onRefresh }) {
    const total = STATUSES.reduce((acc, s) => acc + (counts[s] || 0), 0);
    return (
        <div data-testid="quotes-admin">
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                    type="button"
                    onClick={() => onFilterChange({ ...filter, status: "" })}
                    className={
                        "bl-badge " +
                        (filter.status === "" ? "bl-badge-neutral" : "")
                    }
                    data-testid="quote-filter-status-all"
                >
                    All · {total}
                </button>
                {STATUSES.map((s) => (
                    <button
                        type="button"
                        key={s}
                        onClick={() => onFilterChange({ ...filter, status: s })}
                        className={
                            "bl-badge " +
                            (filter.status === s ? STATUS_STYLE[s] : "")
                        }
                        data-testid={`quote-filter-status-${s}`}
                    >
                        {s} · {counts[s] || 0}
                    </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                    <input
                        type="text"
                        inputMode="numeric"
                        value={filter.zip_code}
                        onChange={(e) =>
                            onFilterChange({
                                ...filter,
                                zip_code: e.target.value.replace(/\D/g, "").slice(0, 5),
                            })
                        }
                        placeholder="Filter by ZIP"
                        className="px-3 py-1.5 bg-bl-surface rounded-lg border border-bl text-bl text-xs outline-none focus:border-bl-primary w-32"
                        data-testid="quote-filter-zip-input"
                    />
                    <a
                        href={adminExportQuotesCsvUrl({
                            ...(filter.status ? { status: filter.status } : {}),
                            ...(filter.zip_code ? { zip_code: filter.zip_code } : {}),
                        })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bl-btn-ghost flex items-center gap-1.5 text-xs"
                        data-testid="quote-export-csv-link"
                        title="Download filtered quotes as CSV"
                    >
                        <Download className="w-3.5 h-3.5" /> Export CSV
                    </a>
                </div>
            </div>
            <div className="bl-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            {[
                                "Category",
                                "Buyer",
                                "Phone",
                                "ZIP",
                                "Status",
                                "Routed to",
                                "Created",
                                "",
                            ].map((h) => (
                                <th
                                    key={h}
                                    className="px-5 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {quotes.length === 0 && (
                            <tr>
                                <td
                                    colSpan={8}
                                    className="px-5 py-8 text-center text-bl-muted"
                                >
                                    No quote requests match this filter.
                                </td>
                            </tr>
                        )}
                        {quotes.map((q) => (
                            <QuoteRow
                                key={q.quote_id}
                                quote={q}
                                onChanged={onRefresh}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
